import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryDown } from 'components/utils/breakpoint'
import CSSBreakpoint from '../../utils/CSSBreakpoint'

const Container = styled(Group)`
  padding: ${rem(8)};

  &.clickable {
    cursor: pointer;
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
      border-radius: ${props => props.theme.borderRadius.S};
    }
  }

  ${mediaQueryDown.mobile} {
    padding: ${rem(4)} 0;
  }
`

const LabelGroup = styled(Group)`
  width: ${rem(120)};
`

const ProgressBar = styled.div<{ $width?: number }>`
  width: ${props => rem(props.$width || 190)};
  height: ${rem(6)};
  background: linear-gradient(
    to right,
    ${props => props.theme.palette.brand.primary.normal} 0% var(--progress, 0%),
    ${props => props.theme.palette.neutral.default.five} var(--progress, 0%) 100%
  );
  border-radius: ${props => props.theme.borderRadius.round};
`

interface Props {
  label: string
  range: string
  progress: number
  count: number
  isPanel?: boolean
  width?: number
  onClick?: () => void
}

function RatingDistribution({ label, range, progress, count, isPanel = false, width = 190, onClick }: Props) {
  const intl = useIntl()
  const clickable = !!onClick

  const labelWithColon = intl.formatMessage(
    {
      id: 'T68LykwDOM',
      defaultMessage: '{label}:',
      description: 'Rating category label followed by a colon in rating distribution',
    },
    { label },
  )

  if (isPanel) {
    return (
      <Container direction="vertical" verticalAlign="center" gap={4} className={clickable ? 'clickable' : undefined} onClick={onClick}>
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
          <LabelGroup direction="horizontal" verticalAlign="center" gap={4}>
            <BodyText variant="medium" weight="bold">{labelWithColon}</BodyText>
            <BodyText variant="small">{range}</BodyText>
          </LabelGroup>
          <BodyText variant="medium">{count}</BodyText>
        </Group>
        <ProgressBar $width={width} style={{ '--progress': `${progress}%` } as React.CSSProperties} />

      </Container>
    )
  }

  return (
    <>
      {/* Graph Section - Desktop */}
      <CSSBreakpoint min="tablet">
        <Container direction="horizontal" verticalAlign="center" gap={8} className={clickable ? 'clickable' : undefined} onClick={onClick}>
          <LabelGroup direction="horizontal" verticalAlign="center" gap={4}>
            <BodyText variant="medium" weight="bold">{labelWithColon}</BodyText>
            <BodyText variant="medium">{range}</BodyText>
          </LabelGroup>
          <ProgressBar style={{ '--progress': `${progress}%` } as React.CSSProperties} />
          <BodyText variant="medium">{count}</BodyText>
        </Container>
      </CSSBreakpoint>
      {/* Graph Section - Mobile */}
      <CSSBreakpoint max="mobile">
        <Container direction="horizontal" verticalAlign="center" gap={8} className={clickable ? 'clickable' : undefined} onClick={onClick}>
          <LabelGroup direction="horizontal" verticalAlign="center" gap={4}>
            <BodyText variant="small" weight="bold">{labelWithColon}</BodyText>
            <BodyText variant="small">{range}</BodyText>
          </LabelGroup>
          <ProgressBar style={{ '--progress': `${progress}%` } as React.CSSProperties} />
          <BodyText variant="small">{count}</BodyText>
        </Container>
      </CSSBreakpoint>
    </>
  )
}

export default RatingDistribution
